interface BlogPaginationProps {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  className?: string;
}

export function BlogPagination({
  currentPage,
  totalPages,
  onPageChange,
  className = ''
}: BlogPaginationProps) {
  // Generate page numbers to show (1, 2, 3, ..., 8, 9, 10)
  const getPageNumbers = () => {
    const pageNumbers = [];

    // Always include first page
    pageNumbers.push(1);

    // Current page neighborhood
    for (
      let i = Math.max(2, currentPage - 1);
      i <= Math.min(totalPages - 1, currentPage + 1);
      i++
    ) {
      pageNumbers.push(i);
    }

    // Always include last page if there are more than 1 page
    if (totalPages > 1) {
      pageNumbers.push(totalPages);
    }

    // Add ellipsis indices where needed
    const result = [];
    let previous = 0;

    for (const page of pageNumbers) {
      if (page - previous > 1) {
        result.push(-1); // -1 represents ellipsis
      }
      result.push(page);
      previous = page;
    }

    return result;
  };

  const pageNumbers = getPageNumbers();

  // Always show pagination, even if there's only one page
  const actualTotalPages = Math.max(1, totalPages);

  return (
    <div
      className={`mt-8 flex w-full items-center justify-between border-t border-[#EAECF0] pt-5 ${className}`}
    >
      <button
        onClick={() => onPageChange(Math.max(1, currentPage - 1))}
        disabled={currentPage === 1}
        className={`flex items-center gap-2 px-4 py-2 ${
          currentPage === 1
            ? 'cursor-not-allowed text-gray-400'
            : 'text-[#667085] hover:text-gray-900'
        }`}
      >
        <svg
          width="16"
          height="16"
          viewBox="0 0 16 16"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M10.667 12.667L5.33366 8.00033L10.667 3.33366"
            stroke="currentColor"
            strokeWidth="1.6"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
        <span className="text-sm font-medium">Previous</span>
      </button>

      <div className="hidden items-center gap-0.5 sm:flex">
        {pageNumbers.map((page, index) =>
          page === -1 ? (
            <span
              key={`ellipsis-${index}`}
              className="flex h-10 w-10 items-center justify-center text-sm text-[#667085]"
            >
              ...
            </span>
          ) : (
            <button
              key={page}
              onClick={() => onPageChange(page)}
              className={`flex h-10 w-10 items-center justify-center rounded-lg text-sm font-medium ${
                currentPage === page
                  ? 'bg-[#F9F5FF] text-[#7F56D9]'
                  : 'text-[#667085] hover:bg-gray-100'
              }`}
            >
              {page}
            </button>
          )
        )}
      </div>

      {/* Mobile pagination indicator */}
      <div className="text-sm text-[#667085] sm:hidden">
        Page {currentPage} of {actualTotalPages}
      </div>

      <button
        onClick={() => onPageChange(Math.min(actualTotalPages, currentPage + 1))}
        disabled={currentPage === actualTotalPages}
        className={`flex items-center gap-2 px-4 py-2 ${
          currentPage === actualTotalPages
            ? 'cursor-not-allowed text-gray-400'
            : 'text-[#667085] hover:text-gray-900'
        }`}
      >
        <span className="text-sm font-medium">Next</span>
        <svg
          width="16"
          height="16"
          viewBox="0 0 16 16"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M5.33366 3.33366L10.667 8.00033L5.33366 12.667"
            stroke="currentColor"
            strokeWidth="1.6"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>
    </div>
  );
}
